/* What Connection History says, in one place, so the headline, the rows and
 * the list of what happened cannot disagree. A laptop or phone names its paths
 * by kind (wifi, cellular, ethernet), numbered from the second of a kind. */

const KINDS = { wifi: "Wi-Fi", cellular: "Cellular", ethernet: "Ethernet" };

/** pathLabel is a path's name as a person says it: "wifi2" is "Wi-Fi 2". */
export function pathLabel(name) {
  for (const [kind, word] of Object.entries(KINDS)) {
    if (name?.startsWith(kind)) {
      const rest = name.slice(kind.length);
      return rest ? `${word} ${rest}` : word;
    }
  }
  return name || "A network";
}

/** clock is a time of day to the tenth of a second, which is how closely the
 *  page and the device's own log can be compared. */
export function clock(ts) {
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return "";
  const hms = d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false });
  return `${hms}.${Math.floor(d.getMilliseconds() / 100)}`;
}

/** seconds is a short span as a person reads it: "0.4 s", "12 s". */
export function seconds(ms) {
  if (!Number.isFinite(ms)) return "";
  return ms < 10000 ? `${(ms / 1000).toFixed(1)} s` : `${Math.round(ms / 1000)} s`;
}

/** rate is bytes per second as a speed. */
export function rate(bytesPerSec) {
  const mbit = (Number(bytesPerSec) || 0) * 8 / 1e6;
  return mbit >= 10 ? `${Math.round(mbit)} Mbit/s` : `${mbit.toFixed(1)} Mbit/s`;
}

/** signalWords places a reading on the 0 to 31 mobile broadband scale. */
export function signalWords(signal) {
  if (!Number.isFinite(signal)) return "not reported";
  const band = signal >= 20 ? "strong" : signal >= 15 ? "good" : signal >= 10 ? "fair" : "weak";
  return `${band}, ${signal} of 31`;
}

/** STATES are a path's state now, in words, with the tone they are drawn in. */
export const STATES = {
  working: { word: "Working", tone: "good" },
  silent: { word: "Silent: nothing gets through", tone: "bad" },
  disconnected: { word: "Disconnected", tone: "idle" },
};

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

// wentDown is how a path left, as a move event records it.
const wentDown = (tags) => (tags?.down === "gone" ? "disconnected" : "went silent");

/** happening tells one event as a sentence, or returns null for an event this
 *  page does not tell. wasSilent says whether this path's last event before it
 *  had it go silent, which makes a return "came back" rather than "connected". */
export function happening(event, wasSilent) {
  const path = pathLabel(event.tags?.wan);
  switch (event.type) {
    case "wan_down":
      return event.tags?.cause === "gone"
        ? { text: `${path} disconnected.`, tone: "idle" }
        : { text: `${path} went silent: nothing got through it.`, tone: "bad" };
    case "wan_up":
      return wasSilent
        ? { text: `${path} came back.`, tone: "good" }
        : { text: `${path} connected.`, tone: "good" };
    case "failover": {
      const to = event.tags?.to;
      if (!to) return { text: `Traffic moved off ${path}.`, tone: "move" };
      const flows = event.fields?.flows;
      const what = Number.isFinite(flows) ? `Its ${plural(flows, "connection", "connections")}` : "Its connections";
      const after = Number.isFinite(event.fields?.after_ms) ? `, ${seconds(event.fields.after_ms)} after ${path} ${wentDown(event.tags)}` : "";
      return { text: `${what} moved to ${pathLabel(to)}${after}.`, tone: "move" };
    }
    case "cloud_switch":
      return { text: "The tunnel moved to another anchor.", tone: "move" };
    default:
      return null;
  }
}

/** headline is the page's first sentence: what the device is connected over
 *  now. heard is when it last reported, staleAfter how long a silence means it
 *  has stopped reporting. */
export function headline(rows, heard, now, staleAfter) {
  if (!heard) return "Nothing reported in this window.";
  if (now - heard > staleAfter) return `Not heard from since ${clock(heard)}.`;
  const named = (state) => rows.filter((r) => r.state === state).map((r) => r.label);
  const working = named("working");
  const silent = named("silent");
  if (working.length === 0) {
    return silent.length ? "Connected, but every network is silent." : "Connected, waiting for a network.";
  }
  const over = `Connected over ${working.join(" and ")}${silent.length ? " only" : ""}.`;
  return silent.length ? `${over} ${silent.join(" and ")} ${silent.length === 1 ? "is" : "are"} silent.` : over;
}

/** lastMove is the newest move in a sentence: when a path went down and
 *  where its connections went. */
export function lastMove(events) {
  const move = [...events].reverse().find((e) => e.type === "failover" && e.tags?.to);
  if (!move) return "";
  const at = new Date(move.ts).getTime() - (move.fields?.after_ms || 0);
  const flows = move.fields?.flows;
  const what = Number.isFinite(flows) ? `its ${plural(flows, "connection", "connections")}` : "its connections";
  return `${pathLabel(move.tags.wan)} ${wentDown(move.tags)} at ${clock(at)} and ${what} moved to ${pathLabel(move.tags.to)} ${seconds(move.fields?.after_ms)} later.`;
}
